import { useEffect, useMemo, useRef, useState } from "react";

/* ---------- tiny utils ---------- */
function fill(template, vars) {
  return template.replace(/\{(\w+)\}/g, (_, k) => (vars && vars[k]) || "");
}

/* ---------- dynamic info extracted from text (prompt or stream) ---------- */
function extractFromText(t) {
  if (!t) return null;

  // 1) Section headers (generic + Vintari sections)
  const section =
    t.match(/^(#+)\s*([^\n]+)/m)?.[2] ||
    t.match(
      /^(?:\d+\.\s+|\*\*\s*)(Clarify the Core Need|Fresh Strategic Angles|Risks & Reality Checks|Start → Test → Expand|Playbook|Roadmap)[^\n]*/im
    )?.[0];
  if (section) {
    const clean = section.replace(/^(\*\*|\d+\.\s+)/, "").trim();
    return { stage: "structuring", text: `Structuring: ${clean}`, vars: { topic: clean } };
  }

  // 2) Code fence language
  const codeLang = t.match(/```([\w+-]+)/)?.[1];
  if (codeLang) return { stage: "coding", text: `Composing ${codeLang}…`, vars: { lang: codeLang } };

  // 3) “A vs B”
  const vs = t.match(/\b([\w .&/:-]{2,60})\s+vs\.?\s+([\w .&/:-]{2,60})\b/i);
  if (vs) {
    const a = vs[1].trim();
    const b = vs[2].trim();
    return { stage: "comparing", text: `Comparing ${a} vs ${b}…`, vars: { a, b, topic: `${a} vs ${b}` } };
  }

  // 4) Email subject
  const subj = t.match(/^\s*subject:\s*(.+)$/im)?.[1];
  if (subj) return { stage: "email", text: `Framing email: “${subj.trim()}”`, vars: { topic: subj.trim() } };

  // 5) Risks & mitigations together
  if (/risks?\b/i.test(t) && /mitigation|countermeasure|fallback/i.test(t))
    return { stage: "risk", text: "Scanning risks & mitigations…", vars: {} };

  // 6) Playbook / phases
  if (/playbook|phase|milestones?|roadmap/i.test(t)) {
    const bullet = t.match(/^\s*[-•]\s*([A-Za-z0-9][^\n]{0,60})/m)?.[1]?.trim();
    return {
      stage: "planning",
      text: bullet ? `Sequencing: ${bullet}…` : "Sequencing next steps…",
      vars: { topic: bullet || "" },
    };
  }

  // 7) Math / metrics
  if (/%|\bROI\b|\b(\d+\s*\/\s*\d+)\b/.test(t)) {
    const pct = t.match(/\b\d{1,3}(?:\.\d+)?%/g)?.slice(-1)?.[0];
    return { stage: "math", text: pct ? `Crunching numbers (~${pct})…` : "Crunching numbers…", vars: { pct: pct || "" } };
  }

  // 8) Citations/sources
  if (/\[\d+\]/.test(t) || /###\s*Sources/i.test(t)) {
    return { stage: "research", text: "Linking sources…", vars: {} };
  }

  return null;
}

/* ---------- seeded helpers for varied wording ---------- */
function seedFrom(str = "") {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function pick(arr, seed, salt) {
  if (!arr.length) return "";
  const x = (seed ^ salt) >>> 0;
  return arr[x % arr.length];
}

/* ---------- ONE-TIME ordered plan from the prompt (no rotation) ---------- */
function buildPreStreamPlan(prompt = "") {
  if (!prompt) return [];

  const plan = [];
  const seed = seedFrom(prompt);

  const pct = prompt.match(/\b\d{1,3}(?:\.\d+)?%/g)?.slice(-1)?.[0];
  const mrr = prompt.match(/\$?\s?\d[\d,]*(?:\.\d+)?\s*(?:MRR|ARR)\b/i)?.[0];
  const horizon = prompt.match(/\bnext\s+(\d+)\s*(days?|weeks?|months?|quarters?)\b/i);
  const hasChurn = /churn|retention/i.test(prompt);
  const hasAcq = /acquisition|CAC|paid|channels?|growth/i.test(prompt);
  const hasConstraint = /resource|constraint|one thing|priority|budget|headcount/i.test(prompt);
  const vs = prompt.match(/\b([\w .&/:-]{2,60})\s+vs\.?\s+([\w .&/:-]{2,60})\b/i);

  // 1) Frame objective
  plan.push(
    pick(
      [
        "Clarifying decision objective…",
        "Locking onto the core question…",
        "Pinning down the decision we must make…",
      ],
      seed,
      1
    )
  );

  // 2) Quant context
  if (mrr) plan.push(`Reading revenue context (${mrr.trim()})…`);
  if (pct) plan.push(`Parsing risk metric (~${pct})…`);

  // 3) Time window
  if (horizon) plan.push(`Anchoring analysis to ${horizon[1]} ${horizon[2]}…`);

  // 4) Constraints
  if (hasConstraint)
    plan.push(
      pick(
        [
          "Mapping constraints (focus on one initiative)…",
          "Accounting for limited execution capacity…",
          "Scoping to a single 90-day bet…",
        ],
        seed,
        2
      )
    );

  // 5) Competing paths
  if (vs) {
    plan.push(`Framing tradeoff: ${vs[1].trim()} vs ${vs[2].trim()}…`);
  } else if (hasChurn && hasAcq) {
    plan.push("Framing tradeoff: retention vs acquisition…");
  }

  // 6) Impact model pieces
  if (hasChurn)
    plan.push(
      pick(
        [
          "Estimating retention levers → cohorts, onboarding, value cadence…",
          "Sizing churn drivers → cohorts, UX friction, value gaps…",
        ],
        seed,
        3
      )
    );
  if (hasAcq)
    plan.push(
      pick(
        [
          "Surveying acquisition levers → channels, CAC, conversion rates…",
          "Checking acquisition engine → channel mix, CAC payback, funnel CVRs…",
        ],
        seed,
        4
      )
    );

  // 7) Compare ROI in the time window
  plan.push(
    pick(
      [
        "Comparing 90-day ROI across options…",
        "Ranking options by 90-day expected impact…",
        "Estimating near-term ROI to break the tie…",
      ],
      seed,
      5
    )
  );

  // 8) Risks
  plan.push(
    pick(
      [
        "Stress-testing downside risks and reversibility…",
        "Scanning risks, dependencies, and reversibility…",
        "Checking risk profile & reversibility of each path…",
      ],
      seed,
      6
    )
  );

  // 9) Decision rule / recommended path
  plan.push(
    pick(
      [
        "Applying decision rule → recommend one path…",
        "Converging on a recommendation with rationale…",
        "Selecting the higher-confidence path and why…",
      ],
      seed,
      7
    )
  );

  // Dedup, keep order, cap
  const seen = new Set();
  const unique = [];
  for (const line of plan) {
    if (!line || seen.has(line)) continue;
    seen.add(line);
    unique.push(line);
  }
  return unique.slice(0, 9);
}

/* ---------- weak heuristic (only if idle) ---------- */
function pickStage(buffer = "") {
  const stageMatchers = [
    ["coding", [/```[\w-]*/i, /\b(import|function|const|class)\b/]],
    ["math", [/=|≈|±|≥|≤|\b\d+(\.\d+)?%/, /\b(\d+(\.\d+)?\s*\/\s*\d+)/]],
    ["planning", [/\b(roadmap|playbook|phases?|milestones?)\b/i]],
    ["risk", [/\brisks?\b|\bmitigation\b|\btrade[- ]?offs?\b/i]],
    ["comparing", [/\bvs\.?\b|\bagainst\b|\bcompared to\b/i]],
    ["brainstorming", [/\bideas?:\b|\boptions?:\b|^- /im]],
    ["research", [/\bcitations?:\b|\bsources?:\b|\breferences?:\b|\[\d+\]/i]],
    ["email", [/\bsubject:\b|\bhi\s|hello|dear\b/i]],
    ["structuring", [/^#{1,3}\s|\*\*.+\*\*|^\d+\./m]],
    ["summarizing", [/\bsummary\b|\btl;dr\b/i]],
    ["refining", [/\brefine(d|ment)?\b|\bpolish(ed)?\b/i]],
    ["analyzing", [/\bbecause\b|\btherefore\b|\bso that\b|\bdrivers?\b/i]],
  ];
  for (const [stage, regs] of stageMatchers) {
    if (regs.every((r) => r.test(buffer))) return stage;
    if (regs.some((r) => r.test(buffer))) return stage;
  }
  return null;
}

/* ---------- one neutral phrase pack (mode-agnostic) ---------- */
const stylePhrases = {
  Normal: {
    analyzing: ["Thinking…"],
    structuring: ["Organizing sections…"],
    comparing: ["Weighing {topic}…"],
    planning: ["Sequencing {topic}…"],
    math: ["Crunching numbers…"],
    coding: ["Composing {lang}…"],
    research: ["Linking sources…"],
    risk: ["Scanning for edge cases…"],
    email: ["Framing the email…"],
    summarizing: ["Condensing takeaways…"],
    refining: ["Polishing phrasing…"],
  },
};

function phraseFor(stage, vars) {
  const choices = (stylePhrases.Normal[stage] || []).concat([]);
  if (choices.length === 0) return "Thinking…";
  return fill(choices[0], vars); // no rotating filler
}

/* ---------- main hook ---------- */
export default function useAdaptiveStatus({
  isGenerating,
  streamedText,
  promptText,
  updateEveryMs = 1400,
  minPromptChars = 60,
}) {
  const [status, setStatus] = useState("");
  const preIdxRef = useRef(0);

  const dynFromStream = useMemo(() => extractFromText(streamedText), [streamedText]);
  const dynFromPrompt = useMemo(() => extractFromText(promptText), [promptText]);

  // ✅ use the ONE-TIME plan
  const preStreamPlan = useMemo(() => buildPreStreamPlan(promptText || ""), [promptText]);

  const weakStage = useMemo(() => pickStage(streamedText), [streamedText]);

  useEffect(() => {
    if (!isGenerating) {
      setStatus("");
      preIdxRef.current = 0;
      return;
    }

    // 1) Stream signal → exact, stable
    if (dynFromStream?.text) {
      setStatus(dynFromStream.text);
      return;
    }

    // 2) While waiting for first token: advance through the prompt plan ONCE, no wrap
    if ((promptText?.length || 0) >= minPromptChars && preStreamPlan.length > 0) {
      const idx = Math.min(preIdxRef.current, preStreamPlan.length - 1);
      setStatus(preStreamPlan[idx]);

      const id = setInterval(() => {
        preIdxRef.current += 1;
        const next = Math.min(preIdxRef.current, preStreamPlan.length - 1);
        setStatus(preStreamPlan[next]);
        if (next >= preStreamPlan.length - 1) clearInterval(id);
      }, updateEveryMs);

      return () => clearInterval(id);
    }

    // 3) Single prompt signal (no rotation)
    if (dynFromPrompt?.text) {
      setStatus(dynFromPrompt.text.replace(/^Structuring:/, "Analyzing:").trim());
      return;
    }

    // 4) No signal and no stream yet
    if (!streamedText || streamedText.length === 0) {
      setStatus("Thinking…");
      return;
    }

    // 5) Weak heuristic
    if (weakStage) {
      setStatus(phraseFor(weakStage, dynFromPrompt?.vars || {}));
      return;
    }

    setStatus("Thinking…");
  }, [
    isGenerating,
    streamedText,
    promptText,
    dynFromStream,
    dynFromPrompt,
    preStreamPlan,
    weakStage,
    updateEveryMs,
  ]);

  return status;
}
